import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin, Modal } from 'antd';
import { useAuth } from './contexts/AuthContext';
import useMobile from './hooks/useMobile';
import MainLayout from './components/Layout';
import MobileLayout from './components/MobileLayout';

const LoginPage = lazy(() => import('./pages/Login'));
const DashboardPage = lazy(() => import('./pages/Dashboard'));
const ProductsPage = lazy(() => import('./pages/Products'));
const PurchasesPage = lazy(() => import('./pages/Purchases'));
const InventoryPage = lazy(() => import('./pages/Inventory'));
const SuppliersPage = lazy(() => import('./pages/Suppliers'));
const ReportsPage = lazy(() => import('./pages/Reports'));
const CustomerOrdersPage = lazy(() => import('./pages/CustomerOrders'));
const NotificationsPage = lazy(() => import('./pages/Notifications'));
const UserManagementPage = lazy(() => import('./pages/UserManagement'));
const OperationLogsPage = lazy(() => import('./pages/OperationLogs'));
const ServerStatusPage = lazy(() => import('./pages/ServerStatus'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const MobileDashboard = lazy(() => import('./pages/mobile/MobileDashboard'));
const MobileOrders = lazy(() => import('./pages/mobile/MobileOrders'));
const MobileOrderDetail = lazy(() => import('./pages/mobile/MobileOrderDetail'));
const MobileScan = lazy(() => import('./pages/mobile/MobileScan'));
const MobileProfile = lazy(() => import('./pages/mobile/MobileProfile'));
const MobilePurchases = lazy(() => import('./pages/mobile/MobilePurchases'));
const MobilePurchaseDetail = lazy(() => import('./pages/mobile/MobilePurchaseDetail'));
const MobilePurchaseItemDetail = lazy(() => import('./pages/mobile/MobilePurchaseItemDetail'));
const MobileInventory = lazy(() => import('./pages/mobile/MobileInventory'));
const MobileOutbound = lazy(() => import('./pages/mobile/MobileOutbound'));
const CustomizationPage = lazy(() => import('./pages/customization/TemplateList'));
const CustomizationEditor = lazy(() => import('./pages/customization/TemplateEditor'));
const BuyerCustomizer = lazy(() => import('./pages/customization/BuyerCustomizer'));

const loadingEl = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
);

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return loadingEl;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return loadingEl;
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AdminWarehouseRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return loadingEl;
  if (user?.role !== 'admin' && user?.role !== 'warehouse') return <Navigate to="/" replace />;
  return children;
}

function CustomerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return loadingEl;
  if (user?.role === 'customer') return children;
  return <Navigate to="/" replace />;
}

function NonCustomerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return loadingEl;
  if (user?.role === 'customer') return <Navigate to="/customer-orders" replace />;
  return children;
}

function DashboardRedirect() {
  const { user } = useAuth();
  if (user?.role === 'customer') return <Navigate to="/customer-orders" replace />;
  return <DashboardPage />;
}

class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2>页面错误</h2>
        <pre style={{ textAlign: 'left', background: '#f5f5f5', padding: 16, borderRadius: 8, maxWidth: 600, margin: '0 auto', fontSize: 12, overflow: 'auto' }}>
          {this.state.error.message + '\n' + (this.state.error.stack || '')}
        </pre>
        <button onClick={() => window.location.href = '/customization'} style={{ marginTop: 16 }}>返回列表</button>
      </div>
    );
    return this.props.children;
  }
}

export default function App() {
  const { isAuthenticated, loading } = useAuth();
  const isMobile = useMobile();
  const versionRef = useRef(null);
  const notifiedRef = useRef(false);

  // 版本检测：用户切回页面时检查index.html是否变化
  useEffect(() => {
    if (!isAuthenticated) return;
    // 记录初始版本的main.js路径
    const checkVersion = async () => {
      try {
        const res = await fetch('/index.html', { cache: 'no-store' });
        const html = await res.text();
        const match = html.match(/static\/js\/main\.[\w]+\.js/);
        if (!match) return;
        const currentMain = match[0];
        if (!versionRef.current) {
          versionRef.current = currentMain;
          return;
        }
        if (versionRef.current !== currentMain && !notifiedRef.current) {
          notifiedRef.current = true;
          Modal.info({
            title: '发现新版本',
            content: '系统已更新，请刷新页面加载最新版本',
            okText: '立即刷新',
            onOk: () => { window.location.reload(true); },
          });
        }
      } catch (e) { /* 忽略网络错误 */ }
    };
    // 页面可见性变化时检查（用户从其他标签页切回来）
    const handler = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', handler);
    // 每10分钟定时检查一次
    const timer = setInterval(checkVersion, 10 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      clearInterval(timer);
    };
  }, [isAuthenticated]);

  if (loading) return loadingEl;

  return (
    <Suspense fallback={loadingEl}>
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to={isMobile ? '/mobile' : '/'} replace /> : <LoginPage />}
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              {isMobile ? <Navigate to="/mobile" replace /> : <MainLayout />}
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardRedirect />} />
          <Route path="products" element={<AdminWarehouseRoute><ProductsPage /></AdminWarehouseRoute>} />
          <Route path="purchases" element={<PurchasesPage />} />
          <Route path="inventory" element={<AdminWarehouseRoute><InventoryPage /></AdminWarehouseRoute>} />
          <Route path="suppliers" element={<AdminWarehouseRoute><SuppliersPage /></AdminWarehouseRoute>} />
          <Route path="reports" element={<AdminWarehouseRoute><ReportsPage /></AdminWarehouseRoute>} />
          <Route path="customer-orders" element={<CustomerOrdersPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
          <Route path="server-status" element={<AdminRoute><ServerStatusPage /></AdminRoute>} />
          <Route path="operation-logs" element={<AdminWarehouseRoute><OperationLogsPage /></AdminWarehouseRoute>} />
          <Route path="customization" element={<AdminRoute><CustomizationPage /></AdminRoute>} />
          <Route path="customization/:id/edit" element={<AdminRoute><ErrorBoundary><CustomizationEditor /></ErrorBoundary></AdminRoute>} />
          <Route path="customization/new" element={<AdminRoute><ErrorBoundary><CustomizationEditor /></ErrorBoundary></AdminRoute>} />
          <Route path="order/:orderId/customize" element={<ProtectedRoute><BuyerCustomizer /></ProtectedRoute>} />
          <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        </Route>
        {/* 移动端路由 */}
        <Route
          path="/mobile"
          element={
            <ProtectedRoute>
              <MobileLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MobileDashboard />} />
          <Route path="orders" element={<MobileOrders />} />
          <Route path="orders/:id" element={<MobileOrderDetail />} />
          <Route path="scan" element={<MobileScan />} />
          <Route path="purchases" element={<MobilePurchases />} />
          <Route path="purchases/:id" element={<MobilePurchaseDetail />} />
          <Route path="purchases/:id/item/:itemId" element={<MobilePurchaseItemDetail />} />
          <Route path="inventory" element={<MobileInventory />} />
          <Route path="outbound" element={<MobileOutbound />} />
          <Route path="profile" element={<MobileProfile />} />
        </Route>
        <Route path="*" element={<Navigate to={isMobile ? '/mobile' : '/'} replace />} />
      </Routes>
    </Suspense>
  );
}
